import { useQuery } from '@apollo/client/react';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { fetchBids } from '../../queries/bid';

interface ModalProps {
    open: boolean;
    type: string;
    onDismiss: (data?: any, role?: string) => void;
}

const ModalPickUpBid: React.FC<ModalProps> = ({ open, onDismiss, type }) => {
    const [itemChosen, setItemChosen] = useState<any>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const bids = useRef<any>([]);
    const [filteredBids, setFilteredBids] = useState<any>([])

    const handlePickItem = (el: any) => {
        if (itemChosen === el) {
            setItemChosen(null);
        } else {
            setItemChosen(el);
        }
    };

    const { loading, error, data } = useQuery(fetchBids, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
    });

    useEffect(() => {
        if (data) {
            const bidData = (data as any).data;
            if (bidData) {
                bids.current = bidData;
                setFilteredBids(bidData);
            }
        }
    }, [data]);

    useEffect(() => {
        if (error) {
            console.error('Error fetching bids:', error);
        }
    }, [error]);

    useEffect(() => {
        if (searchTerm?.length > 0) {
            setFilteredBids(bids.current.filter((b: any) => (b.organisation_name as string)?.toLowerCase().includes(searchTerm.toLowerCase())));
        } else {
            setFilteredBids(bids.current);
        }
    }, [searchTerm]);

    return (
        <Dialog
            open={open}
            onClose={() => onDismiss(null, 'cancel')}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Search Organization</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                            size="small"
                            placeholder="Search by organization name"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <IconButton
                            edge="end"
                            color="inherit"
                            onClick={() => onDismiss(null, 'cancel')}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error">Error loading organizations: {error.message}</Alert>
                ) : (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="15%">Org ID</TableCell>
                                    <TableCell width="35%">Name</TableCell>
                                    <TableCell width="25%">Price</TableCell>
                                    <TableCell width="25%">Bidder</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredBids?.map((el: any, index: number) => (
                                    <TableRow
                                        key={index}
                                        onClick={() => handlePickItem(el)}
                                        selected={el === itemChosen}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell>{el.organisation_id}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{el.organisation_name}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {el.bids?.at(0)?.price_per_kg || 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary">
                                                {el.bids?.at(0)?.updated_by || 'N/A'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    variant="outlined"
                    onClick={() => onDismiss(null, 'cancel')}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={!itemChosen}
                    onClick={() => onDismiss({
                        chosen: itemChosen,
                    }, 'confirm')}
                >
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModalPickUpBid;